import {ApplicationInitStatus, Injectable} from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpHeaderResponse,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import {Observable} from 'rxjs';
import {NonceAuthService} from '../auth/nonce-auth.service';
import {StateStorageService} from '../services/state-storage.service';
import {mergeMap, tap, timeout} from 'rxjs/operators';
import {HTTP_TIMEOUT} from '../../environments/environment';
import {CommunicationService} from '../services/communication.service';
import {CommonFunctionsService} from "../services/common-functions.service";

@Injectable({
    providedIn: 'root',
})
export class JwtInterceptor implements HttpInterceptor {
    private lastLang: string = '';
    private apiEndPoint = '';

    constructor(private appInit: ApplicationInitStatus,
                private authService: NonceAuthService,
                private appState: StateStorageService,
                private appComm: CommunicationService,
                private commonFunctions: CommonFunctionsService
    ) {
        appInit.donePromise.then(() => {
            this.apiEndPoint = this.appState.apiEndPoint;
        });
    }

    private handleRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let useTimeout = HTTP_TIMEOUT;
        let params = request.params;
        if (params.has('longRequest')) {
            useTimeout = 1000 * HTTP_TIMEOUT;
            params = params.delete('longRequest');
        }
        let silentError: boolean = false;
        if (params.has('silentError')) {
            silentError = true;
            params = params.delete('silentError');
        }
        request = request.clone({params: params});
        return next.handle(request).pipe(
            timeout(useTimeout),
            tap((res) => {
                    if (res instanceof HttpHeaderResponse || res instanceof HttpResponse)
                        this.setTokenAndLangFromHeaders(res.headers);
                },
                (err) => {
                    if (!silentError) this.commonFunctions.showHttpError(err);  // show error before changing logout status
                    if (err.headers) this.setTokenAndLangFromHeaders(err.headers);
                    if (err.status === 401) this.authService.isLoggedOut();
                })
        );
    }

    private setTokenAndLangFromHeaders(headers: HttpHeaders) {
        const newToken = headers.get('Authorization');
        const newLang = headers.get('Content-Language');
        if (newToken) {
            this.appState.nonceMissing = false;
            this.authService.setAuthToken(newToken);
        }
        if (newLang && (newLang !== this.lastLang)) {
            this.lastLang = newLang;
            this.appComm.browserLanguage = newLang;
        }
    }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (('OPTIONS' === request.method) // CORS preflight
            || (request.url.substring(0, 4) !== 'http')) {
            return this.handleRequest(request, next);
        }

        if (request.url.substring(0, this.apiEndPoint.length) !== this.apiEndPoint) {// not our api call
            return this.handleRequest(request, next);
        }

        return this.authService.getAuthToken()
            .pipe(
                mergeMap((authtoken: string) => {
                    if (authtoken) {
                        request = request.clone({
                            setHeaders: {
                                Authorization: authtoken
                            }
                        });
                    }
                    return this.handleRequest(request, next);
                })
            );
    }

}
